import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { ModalController } from '@ionic/angular';
import { SessionService } from 'src/app/services/session/session.service';
import { QuickComponentsService } from 'src/app/services/quick-components/quick-components.service';
import { UiFeedbackService } from 'src/app/services/ui-feedback/ui-feedback.service';

/** El aviso que sale al pulsar «Chat» sin sesión. */
export const AVISO_CHAT_SIN_SESION = 'Inicia sesión para usar el chat';

/** Rutas que, sin sesión, piden entrar en vez de devolver a la portada. */
export function pideAcceso(url: string | null | undefined): boolean {
  return !!url && /^\/chats(\/|$|\?)/.test(url);
}

/**
 * Deja pasar sólo si hay sesión.
 *
 * La versión anterior, además de mirar el usuario, se suscribía a
 * `storage.observeUserStatus()` y NO cancelaba la suscripción. Como detrás hay un
 * `BehaviorSubject` que vive lo que dura la aplicación, cada navegación a una ruta
 * protegida dejaba una suscripción más, y todas llamaban a `navigateByUrl('/')`
 * cuando cambiaba el estado: al cerrar sesión se disparaban tantas redirecciones
 * como veces se hubiera entrado. Los `return true/false` de dentro del callback
 * tampoco hacían nada: eran el valor de retorno del callback, no del guard.
 *
 * Y era frágil por otro lado: `observeUserStatus()` devuelve `null` mientras
 * `StorageService.isBrowser` no se ha resuelto —se fija de forma asíncrona desde
 * `checkIfBrowser()`—, así que el `.subscribe(...)` podía reventar sobre `null`.
 *
 * Un guard sólo tiene que responder una vez, sobre el estado de ese momento. Si la
 * sesión cae después, quien avisa es el evento de logout, no esto.
 *
 * `/chats/` sin sesión (31/10/2026): antes se devolvía a la portada sin decir nada, y
 * pulsar «Chat» desde la portada parecía no hacer nada. Ahora avisa «Inicia sesión para
 * usar el chat», abre el acceso y, si se entra, sigue al chat.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthUserGuard implements CanActivate {
  private router = inject(Router);
  private session = inject(SessionService);
  private platformId = inject(PLATFORM_ID);
  private quick = inject(QuickComponentsService);
  private ui = inject(UiFeedbackService);
  private modalCtrl = inject(ModalController);

  async canActivate(_route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot) {
    // En servidor no hay sesión que mirar: estas rutas son privadas y no se
    // renderizan. Se mantiene el comportamiento de siempre.
    if (!isPlatformBrowser(this.platformId)) {
      return this.router.navigate(['/loading']);
    }

    if (this.haySesion()) return true;

    if (pideAcceso(state?.url)) {
      await this.entrar();
      if (this.haySesion()) return true;

      // Sin entrar: si se venía de otra página, se queda en ella; si se abrió la URL
      // directamente, no hay página detrás y se va a la portada.
      if (this.router.navigated) return false;
    }

    this.router.navigateByUrl('/');
    return false;
  }

  private haySesion(): boolean {
    return !!this.session.mc?.user;
  }

  /**
   * Aviso y acceso; espera a que se cierre. `openLoginBox` vuelve al cerrarse el de login
   * aunque se haya saltado al de registro o al de recuperar contraseña, así que si sigue
   * sin sesión se espera también a los modales que hayan quedado encima, con tope.
   */
  private async entrar() {
    const acceso = this.quick.openLoginBox('login');

    // El aviso, cuando el modal ya está abierto: Ionic apila los overlays por orden de
    // creación, y un aviso creado antes quedaría debajo del acceso (a pantalla completa en móvil).
    for (let i = 0; i < 30 && !(await this.modalCtrl.getTop?.()); i++) {
      await new Promise(r => setTimeout(r, 100));
    }
    void this.ui.showToast(AVISO_CHAT_SIN_SESION, 'warning', 4000);

    try {
      await acceso;
    } catch (error) {
      console.error(error);
      return;
    }

    for (let i = 0; i < 3 && !this.haySesion(); i++) {
      const encima = await this.modalCtrl.getTop?.();
      if (!encima) return;
      await encima.onDidDismiss();
    }
  }
}
